import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, FileText, KeyRound, Loader2, Printer, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FactureDocument, LIGNE_VIDE } from "@/components/factures/FactureDocument";
import { api, errMsg } from "@/lib/api";
import { paysEnAnglais } from "@/lib/pays";

const cleCode = (token) => `facture-code-${token}`;
// Champs obligatoires -> clé (pour encadrer en rouge ce qui manque)
const CLES_OBLIG = ["date_export", "expediteur", "destinataire", "pays_export", "pays_fabrication", "pays_destination"];

/**
 * Page du client (sans compte) : /facture/<jeton>. Il saisit le code
 * d'accès reçu, remplit la facture pro forma, l'enregistre, puis la valide.
 */
export default function FactureClientPage() {
  const { token } = useParams();
  const [params, setParams] = useSearchParams();
  // Le lien de l'e-mail contient le code (?code=…) : le document s'ouvre directement.
  const [code, setCode] = useState(() => {
    const dansLien = (params.get("code") || "").trim().toUpperCase();
    if (dansLien) return dansLien;
    try { return sessionStorage.getItem(cleCode(token)) || ""; } catch { return ""; }
  });
  const [saisieCode, setSaisieCode] = useState("");
  const [f, setF] = useState(null);
  const [champs, setChamps] = useState(null);
  const [erreur, setErreur] = useState("");
  const [busy, setBusy] = useState(false);
  const [pret, setPret] = useState(false); // écran du code affiché seulement si l'ouverture directe a échoué
  const [modifie, setModifie] = useState(false);
  const [confirmer, setConfirmer] = useState(false);
  const [manquants, setManquants] = useState([]);
  const minuterie = useRef(null);

  const ouvrir = useCallback(async (c) => {
    setBusy(true); setErreur("");
    try {
      const { data } = await api.post(`/public/facture/${token}`, { code: c });
      setF(data);
      // Dates pré-remplies au jour (Paris) et pays d'exportation « France », tous modifiables.
      const ch = data.champs || {};
      setChamps({
        ...ch,
        date_export: ch.date_export || data.aujourdhui,
        date_document: ch.date_document || data.aujourdhui,
        // pays toujours inscrits en anglais (une ancienne saisie « Chine » devient « China »)
        pays_export: paysEnAnglais(ch.pays_export || "France"),
        pays_fabrication: paysEnAnglais(ch.pays_fabrication),
        pays_destination: paysEnAnglais(ch.pays_destination),
        lignes: ch.lignes?.length ? ch.lignes : [{ ...LIGNE_VIDE }],
      });
      setCode(c);
      try { sessionStorage.setItem(cleCode(token), c); } catch { /* stockage indisponible */ }
      if (params.get("code")) setParams({}, { replace: true });   // le code n'apparaît plus dans la barre d'adresse
    } catch (e) {
      setErreur(errMsg(e)); setCode(""); setPret(true);
      try { sessionStorage.removeItem(cleCode(token)); } catch { /* stockage indisponible */ }
    } finally { setBusy(false); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (code) ouvrir(code); else setPret(true); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const enregistrer = useCallback(async (silencieux = false) => {
    if (!champs || f?.statut === "cloturee") return;
    try {
      const { data } = await api.put(`/public/facture/${token}`, { code, champs });
      setF((x) => ({ ...x, statut: data.statut, statut_label: data.statut_label }));
      setModifie(false);
      if (!silencieux) toast.success("Brouillon enregistré");
    } catch (e) { if (!silencieux) toast.error(errMsg(e)); }
  }, [champs, code, token, f?.statut]);

  // Enregistrement automatique quelques secondes après chaque modification.
  useEffect(() => {
    if (!modifie) return undefined;
    clearTimeout(minuterie.current);
    minuterie.current = setTimeout(() => enregistrer(true), 4000);
    return () => clearTimeout(minuterie.current);
  }, [champs, modifie, enregistrer]);

  const changer = (c) => { setChamps(c); setModifie(true); };

  const valider = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/public/facture/${token}/valider`, { code, champs });
      setF(data); setChamps(data.champs); setModifie(false); setConfirmer(false); setManquants([]);
      toast.success("Document validé et clôturé");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      const msg = errMsg(e);
      toast.error(msg);
      setConfirmer(false);
      setManquants(CLES_OBLIG.filter((k) => !String(champs?.[k] || "").trim()));
    } finally { setBusy(false); }
  };

  if (!f && !pret) {
    return <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground"><Loader2 className="animate-spin mr-2" size={18} /> Ouverture de la facture…</div>;
  }
  if (!f) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <form onSubmit={(e) => { e.preventDefault(); ouvrir(saisieCode.trim().toUpperCase()); }}
          className="w-full max-w-sm rounded-2xl border border-border bg-card p-6 space-y-4 shadow-sm" data-testid="facture-acces">
          <div className="w-12 h-12 rounded-xl bg-primary/15 text-primary flex items-center justify-center"><FileText size={22} /></div>
          <div>
            <h1 className="font-heading text-xl font-bold">Facture pro forma</h1>
            <p className="text-sm text-muted-foreground">Saisissez le code d'accès reçu avec ce lien.</p>
          </div>
          <div className="relative">
            <KeyRound size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={saisieCode} onChange={(e) => setSaisieCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6))}
              placeholder="CODE" autoFocus autoComplete="one-time-code" className="pl-9 h-12 font-mono text-xl tracking-[0.4em] uppercase" data-testid="facture-code-saisie" />
          </div>
          {erreur && <p className="text-sm text-red-600 dark:text-red-400" data-testid="facture-erreur">{erreur}</p>}
          <Button type="submit" className="w-full h-11" disabled={saisieCode.length < 6 || busy} data-testid="facture-ouvrir">
            {busy && <Loader2 size={15} className="animate-spin" />} Ouvrir la facture
          </Button>
        </form>
      </div>
    );
  }

  const cloturee = f.statut === "cloturee";
  return (
    <div className="min-h-screen bg-neutral-100 dark:bg-neutral-900 print:bg-white py-4 sm:py-8 px-2 sm:px-4">
      <div className="max-w-[210mm] mx-auto mb-4 print:hidden">
        {cloturee ? (
          <div className="rounded-xl border border-emerald-500/40 bg-emerald-50 dark:bg-emerald-500/10 p-4 flex flex-wrap items-center gap-3" data-testid="facture-validee">
            <CheckCircle2 className="text-emerald-600 shrink-0" />
            <div className="flex-1 min-w-[200px] text-sm">
              <p className="font-semibold">Document validé et clôturé.</p>
              <p className="text-muted-foreground">Imprimez-le, signez-le à la main et joignez-le à votre envoi.</p>
            </div>
            <Button onClick={() => window.print()} data-testid="facture-imprimer-client"><Printer size={15} /> Imprimer</Button>
          </div>
        ) : (
          <div className="rounded-xl border border-border bg-card p-4 text-sm flex flex-wrap items-center gap-3">
            <p className="flex-1 min-w-[220px]">Remplissez les champs (<span className="text-red-600">*</span> obligatoires), une ligne par article, puis validez. La signature se fait à la main après impression.</p>
            <span className="text-xs text-muted-foreground" data-testid="facture-etat-sauvegarde">{modifie ? "Modifications non enregistrées…" : "Enregistré"}</span>
          </div>
        )}
      </div>

      <FactureDocument champs={champs} date={f.date_document} editable={!cloturee} onChange={changer} devises={f.devises} erreurs={manquants} />

      {!cloturee && (
        <div className="max-w-[210mm] mx-auto mt-4 flex flex-wrap justify-end gap-2 print:hidden">
          <Button variant="outline" onClick={() => enregistrer(false)} data-testid="facture-enregistrer"><Save size={15} /> Enregistrer le brouillon</Button>
          <Button onClick={() => setConfirmer(true)} data-testid="facture-valider"><CheckCircle2 size={15} /> Valider le document et clôturer</Button>
        </div>
      )}

      <Dialog open={confirmer} onOpenChange={(o) => !o && !busy && setConfirmer(false)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Valider et clôturer ?</DialogTitle>
            <DialogDescription>Le document ne pourra plus être modifié (sauf si votre interlocuteur le rouvre). La date du jour sera inscrite en bas à droite.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmer(false)} disabled={busy}>Revenir</Button>
            <Button onClick={valider} disabled={busy} data-testid="facture-confirmer">{busy && <Loader2 size={14} className="animate-spin" />} Valider et clôturer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
